import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Colors } from '../../utils/colors';
import Cart from '../../components/Cart';
import PaymentOptions from '../../components/Payment/PaymentOptions';
import { removeFromCart, updateQuantity as updateCartQuantity, addToCart, clearCart, updateItemPrice } from '../../redux/slices/cartSlice';
import { grantEbookAccess } from '../../redux/slices/ebookAccessSlice';
import { authorizeMultipleDownloads } from '../../redux/slices/ebookDownloadSlice';
import { mercadoPagoService } from '../../services/mercadoPagoService';
import { getPaymentMethodByBin, getInstallmentsByBin, getDeviceId } from '../../services/mercadopagoSdk';
import { priceCart, unitPriceCents, brl } from '../../utils/ebookPricing';
import { purchaseService } from '../../services/purchaseService';
import { authService } from '../../services/authService';
import { courseService } from '../../services/courseService';
import { setCredentials } from '../../redux/slices/authSlice';
import { setPurchasedFromApi } from '../../redux/slices/ebookAccessSlice';
import PaymentResultModal from '../../components/PaymentResultModal';
import Toast from '../../components/Toast';
import { PRODUCTS_IMAGES } from '../../data/checkoutImages';
import {
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
  FiLock,
  FiTarget,
  FiTag,
  FiBookOpen,
  FiMusic,
  FiAward,
  FiCheck,
  FiArrowLeft,
  FiArrowRight,
  FiArrowDown,
  FiShoppingBag,
} from 'react-icons/fi';
import {
  CheckoutContainerStyle,
  CheckoutContent,
  CheckoutHeader,
  CheckoutGrid,
  CheckoutSection,
  CartItemsGrid,
  GoToPaymentButton,
  BackButton,
  StatusMessage,
  PromotionalBox,
  AccountPromptBox,
} from './Checkout.styles';


// Máscara de telefone BR: (XX) XXXXX-XXXX
const formatWhatsapp = (value) => {
  const d = (value || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

// Traduz alguns status_detail de recusa do Mercado Pago (cartão).
function friendlyMpStatus(detail) {
  const map = {
    cc_rejected_insufficient_amount: 'Cartão sem saldo suficiente.',
    cc_rejected_bad_filled_card_number: 'Número do cartão incorreto.',
    cc_rejected_bad_filled_security_code: 'Código de segurança (CVV) incorreto.',
    cc_rejected_bad_filled_date: 'Data de validade incorreta.',
    cc_rejected_high_risk: 'Pagamento recusado por segurança. Tente outro cartão ou pague com PIX.',
    cc_rejected_call_for_authorize: 'Ligue para o seu banco para autorizar a compra e tente de novo.',
    cc_rejected_card_disabled: 'Cartão desabilitado. Fale com o seu banco.',
    cc_rejected_max_attempts: 'Limite de tentativas atingido. Tente outro cartão ou pague com PIX.',
    cc_rejected_duplicated_payment: 'Pagamento duplicado. Use outro cartão se precisar pagar de novo.',
    cc_rejected_other_reason: 'A operadora recusou o pagamento. Tente outro cartão ou pague com PIX.',
  };
  if (!detail) return null;
  return map[detail] || (String(detail).startsWith('cc_rejected') ? 'Pagamento recusado. Tente outro cartão ou pague com PIX.' : null);
}

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state.cart?.items || []);
  const cartTotal = useSelector((state) => state.cart?.totalPrice || 0);
  const token = useSelector((state) => state.auth.token);
  const isAuthenticated = Boolean(token);

  // Sistemas legados (`grantEbookAccess`, `authorizeMultipleDownloads`) são
  // só de eBooks — não devem receber o curso.
  const ebookOnlyItems = cartItems.filter((item) => item.type !== 'course');
  const hasCourseInCart = cartItems.some((item) => item.type === 'course');
  const hasEbookInCart = ebookOnlyItems.length > 0;
  const successDestination = hasCourseInCart ? '/curso-de-fingerstyle' : '/minhas-compras';

  // Texto pós-venda conforme o que foi comprado (curso, eBooks, ou ambos) —
  // evita dizer sempre "eBooks" quando o carrinho também tem o curso.
  const releasedItemsPhrase =
    hasCourseInCart && hasEbookInCart
      ? 'Seu curso e seus eBooks foram liberados e já estão disponíveis para acesso.'
      : hasCourseInCart
        ? 'Seu curso foi liberado e já está disponível para acesso.'
        : 'Seus eBooks foram liberados e estão disponíveis para acesso e download.';
  // Forma nominal ("seu curso e seus eBooks") para usar dentro de frases.
  const purchasedItemsNoun =
    hasCourseInCart && hasEbookInCart
      ? 'seu curso e seus eBooks'
      : hasCourseInCart
        ? 'seu curso'
        : 'seus eBooks';

  // Num combo (curso + eBooks), o sucesso leva para a página do curso — que não
  // lista eBooks. Passamos um aviso para ela exibir um link de "Minhas Compras".
  const successNavOptions =
    hasCourseInCart && hasEbookInCart
      ? { state: { ebooksLiberados: ebookOnlyItems.length } }
      : undefined;

  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [pixCopiaECola, setPixCopiaECola] = useState(null);
  const [paymentTxid, setPaymentTxid] = useState(null);
  // Método exibido no resumo (PIX por padrão; muda ao escolher cartão).
  const [displayMethod, setDisplayMethod] = useState('pix');
  // Preços exibidos conforme o método (PIX vs cartão) — catálogo do front.
  const displayPricing = priceCart(cartItems, displayMethod);
  const displayTotalCents = displayPricing.totalCents;
  const cardPricing = priceCart(cartItems, 'card');

  // Popup de resultado do pagamento (sucesso / erro).
  const [resultModal, setResultModal] = useState(null);
  // Toast (popup rápido) para avisos de carrinho ("item adicionado", etc.).
  const [toast, setToast] = useState(null);
  const showToast = (message) => setToast(message);
  // Qualquer erro de pagamento também vira popup (além do banner inline).
  useEffect(() => {
    if (statusMessage?.type === 'error') {
      setResultModal({
        type: 'error',
        title: 'Não foi possível concluir',
        message: statusMessage.message.replace(/^[^\p{L}\p{N}]+/u, ''),
        actionLabel: 'Tentar novamente',
        onAction: () => setResultModal(null),
      });
    }
  }, [statusMessage]);

  // Preço real do curso (tier atual vindo da loja-api). O loja-api valida o
  // valor do curso contra o preço vigente — por isso NUNCA usamos um preço
  // fixo no frontend ao adicionar o curso ao carrinho.
  const [coursePriceCents, setCoursePriceCents] = useState(null);
  useEffect(() => {
    let active = true;
    courseService
      .getCourse()
      .then((c) => {
        // A loja-api responde { course: { priceCents, ... } }.
        const priceCents = c?.course?.priceCents;
        if (active && typeof priceCents === 'number') setCoursePriceCents(priceCents);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Estado do prompt "criar conta agora" após pagamento anônimo.
  // Quando preenchido, exibe o card de criação de conta no lugar de redirecionar.
  const [pendingAccount, setPendingAccount] = useState(null);
  const [accountPassword, setAccountPassword] = useState('');
  const [accountWhatsapp, setAccountWhatsapp] = useState(''); // apenas dígitos
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [accountError, setAccountError] = useState(null);

  const paymentOptionsRef = useRef(null);

  // Curso exige conta (acesso é server-side, por `userId`). eBooks podem ser
  // anônimos. Em vez de jogar direto no login, mostramos um AVISO explicando —
  // o usuário decide entrar/criar conta ou remover o curso e seguir.
  const [courseAuthNotice, setCourseAuthNotice] = useState(false);
  useEffect(() => {
    setCourseAuthNotice(!isAuthenticated && hasCourseInCart);
  }, [isAuthenticated, hasCourseInCart]);

  // Flag para abortar o polling do PIX quando o usuário cancela.
  const pixAbortRef = useRef(false);

  // Faz polling do status do pagamento MP até aprovar (ou expirar/cancelar).
  const waitMpApproval = async (id, { timeout = 15 * 60 * 1000, interval = 3000 } = {}) => {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      if (pixAbortRef.current) return false; // usuário cancelou o PIX
      try {
        const s = await mercadoPagoService.getStatus(id);
        if (s?.status === 'approved') return true;
        if (s?.status === 'rejected' || s?.status === 'cancelled') return false;
      } catch (e) {
        // ignora erros pontuais no polling
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, interval));
    }
    return false;
  };

  // Registra a compra na loja-api verificando o pagamento no Mercado Pago
  // (itens autoritativos do servidor). Aceita token explícito para o caso de
  // termos acabado de criar conta e ainda não termos o token no Redux.
  const recordPurchaseWithToken = async (tokenToUse, paymentMethod, paymentMeta = {}, contactInfo = {}) => {
    try {
      const mpPaymentId = paymentMeta.mpPaymentId;
      const email = contactInfo.email || paymentMeta.email;
      const cpf = contactInfo.cpf || paymentMeta.cpf;
      const name = contactInfo.name || paymentMeta.name;

      if (!mpPaymentId || !email) {
        console.warn('⚠️ [Checkout] Registro sem mpPaymentId/email — apenas local.');
        return { ok: false };
      }

      // Registra as compras (órfãs por e-mail) — a loja verifica no MP.
      const recordResponse = await mercadoPagoService.record({ mpPaymentId, email, cpf, name });
      console.log('✅ [Checkout] Compras registradas via Mercado Pago:', recordResponse);

      // Se logado: associa as órfãs à conta e atualiza o Redux.
      if (tokenToUse) {
        try {
          await purchaseService.claimOrphanPurchases(tokenToUse, email, cpf);
        } catch (claimErr) {
          console.warn('⚠️ [Checkout] Erro ao reivindicar órfãs (não crítico):', claimErr.message);
        }
        const listResponse = await purchaseService.listMine(tokenToUse);
        dispatch(setPurchasedFromApi(listResponse.purchases || []));
      }
      return { ok: true };
    } catch (err) {
      console.error('❌ [Checkout] Falha ao registrar compra na loja-api:', {
        message: err.message,
        status: err.status,
      });
      return { ok: false, error: err };
    }
  };

  const recordPurchaseOnApi = (paymentMethod, paymentMeta = {}, contactInfo = {}) =>
    recordPurchaseWithToken(token, paymentMethod, paymentMeta, contactInfo);

  // ===== Após pagamento bem-sucedido =====
  // Se logado: registra na API e redireciona após 2s.
  // Se anônimo: registra como órfã e mostra prompt de criação de conta.
  const afterPaymentSuccess = async (paymentMethod, paymentMeta, paymentData) => {
    // Fecha o modal do formulário de pagamento para não cobrir o próximo passo
    // (popup de sucesso ou o card de criar conta na compra anônima).
    paymentOptionsRef.current?.closeModal?.();

    const contactInfo = {
      name: paymentData.name || paymentData.pixData?.name || paymentData.cardholderName || '',
      email: paymentData.email || paymentData.pixData?.email || '',
      cpf: paymentData.cpf || '',
      // WhatsApp informado no pagamento — reaproveitado na criação da conta.
      telefone: (paymentData.telefone || paymentData.pixData?.telefone || '').replace(/\D/g, ''),
    };

    if (token) {
      // Logado: registra na API e abre o popup de resultado.
      const result = await recordPurchaseOnApi(paymentMethod, paymentMeta, contactInfo);
      dispatch({ type: 'cart/clearCart' });
      if (result?.ok) {
        setResultModal({
          type: 'success',
          title: 'Pagamento concluído!',
          message: releasedItemsPhrase,
          actionLabel: hasCourseInCart ? 'Acessar o curso' : 'Ver minhas compras',
          onAction: () => {
            setResultModal(null);
            navigate(successDestination, successNavOptions);
          },
        });
      } else {
        setResultModal({
          type: 'error',
          title: 'Pagamento aprovado, mas houve um problema',
          message:
            'Seu pagamento foi confirmado, mas não conseguimos liberar o acesso automaticamente. Guarde seu comprovante e fale com o suporte que liberamos rapidinho.',
          actionLabel: 'Entendi',
          onAction: () => setResultModal(null),
        });
      }
    } else {
      // Anônimo: registra como órfã, confirma o pagamento num popup e mostra
      // o card de criar conta (para o acesso ficar salvo na conta).
      await recordPurchaseWithToken(null, paymentMethod, paymentMeta, contactInfo);
      setPendingAccount({
        name: contactInfo.name,
        email: contactInfo.email,
        cpf: contactInfo.cpf,
        telefone: contactInfo.telefone,
        paymentMethod,
        ...paymentMeta,
      });
      // Reaproveita o WhatsApp do pagamento — não precisa digitar de novo.
      if (contactInfo.telefone) {
        setAccountWhatsapp(contactInfo.telefone.replace(/\D/g, '').slice(0, 11));
      }
      setResultModal({
        type: 'success',
        title: 'Pagamento concluído!',
        message:
          'Recebemos seu pagamento com sucesso. Crie sua conta para acessar e baixar o que você comprou de qualquer dispositivo.',
        actionLabel: 'Criar minha conta',
        onAction: () => {
          setResultModal(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
      });
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccountError(null);
    if (accountPassword.length < 8) {
      setAccountError('Senha deve ter no mínimo 8 caracteres');
      return;
    }
    if (accountWhatsapp.length < 10 || accountWhatsapp.length > 11) {
      setAccountError('Informe um WhatsApp válido com DDD (ex.: (11) 91234-5678)');
      return;
    }
    setAccountSubmitting(true);
    try {
      const { token: newToken, user } = await authService.register({
        name: pendingAccount.name,
        email: pendingAccount.email,
        password: accountPassword,
        cpf: pendingAccount.cpf,
        whatsapp: accountWhatsapp,
      });
      // setCredentials é chamado DEPOIS do claim+sync para evitar que o
      // useEffect do App.js dispare listMine antes das órfãs serem reivindicadas.

      // 1. Reivindicar compras órfãs registradas com este e-mail (incluindo a que acabou de pagar)
      console.log('👤 [Checkout] Reivindicando compras órfãs após criar conta...');
      try {
        const claimed = await purchaseService.claimOrphanPurchases(newToken, pendingAccount.email, pendingAccount.cpf);
        console.log('✅ [Checkout] Compras órfãs reivindicadas:', claimed.claimed);
      } catch (claimErr) {
        console.warn('⚠️ [Checkout] Erro ao reivindicar órfãs (não crítico):', claimErr.message);
      }

      // 2. Sincronizar compras do localStorage que não foram registradas na API
      console.log('🔄 [Checkout] Sincronizando compras do localStorage após criar conta...');
      try {
        const syncResult = await purchaseService.syncAnonymousPurchasesToAccount(newToken, pendingAccount.email);
        console.log('✅ [Checkout] Sincronização de localStorage concluída:', syncResult);
      } catch (syncErr) {
        console.warn('⚠️ [Checkout] Erro ao sincronizar localStorage (não crítico):', syncErr.message);
      }

      // 3. Carregar compras já com as órfãs reivindicadas e atualizar Redux
      let purchases = [];
      try {
        const allPurchases = await purchaseService.listMine(newToken);
        purchases = allPurchases.purchases || [];
      } catch (listErr) {
        console.warn('⚠️ [Checkout] Erro ao carregar compras:', listErr.message);
      }

      // Só agora seta credenciais → App.js useEffect dispara com dados já prontos
      dispatch(setCredentials({ token: newToken, user }));
      dispatch(setPurchasedFromApi(purchases));

      // Sucesso: limpa carrinho e redireciona
      dispatch({ type: 'cart/clearCart' });
      navigate(successDestination, successNavOptions);
    } catch (err) {
      if (err.status === 409) {
        setAccountError('409');
      } else {
        setAccountError(err.message || 'Falha ao criar conta');
      }
      setAccountSubmitting(false);
    }
  };


  const handleRemoveFromCart = (id) => {
    // O desconto de R$ 20 nas Tablaturas é exclusivo do combo com a Coleção
    // Completa. Então só resetamos o preço ao remover a COLEÇÃO (não o curso).
    if (id === 'colecao-completa-13-1') {
      const tablaturas = cartItems.find((item) => item.id === 'tablaturas');
      if (tablaturas && tablaturas.price < tablaturasPrice) {
        dispatch(updateItemPrice({ id: 'tablaturas', price: tablaturasPrice }));
      }
    }
    dispatch(removeFromCart(id));
  };

  const handleUpdateQuantity = (id, quantity) => {
    dispatch(updateCartQuantity({ id, quantity }));
  };

  const handlePixSelect = () => {
    setStatusMessage(null);
    setDisplayMethod('pix');
    // Scroll para o formulário de pagamento
    setTimeout(() => {
      paymentOptionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCardSelect = () => {
    setStatusMessage(null);
    setDisplayMethod('card');
    // Scroll para o formulário de pagamento
    setTimeout(() => {
      paymentOptionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCancelPayment = () => {
    pixAbortRef.current = true; // aborta o polling do PIX em andamento
    setStatusMessage(null);
    setQrCode(null);
    setPixCopiaECola(null);
    setPaymentTxid(null);
    setIsProcessing(false);
  };

  const handlePaymentConfirm = async (method, data) => {
    if (cartItems.length === 0) {
      setStatusMessage({
        type: 'error',
        message: 'Seu carrinho está vazio',
      });
      return;
    }

    setIsProcessing(true);
    setStatusMessage(null);

    try {
      // Dados do pagador (o preço é SEMPRE decidido no backend pelo catálogo).
      const payer = {
        email: data.email || data.pixData?.email || '',
        name: data.name || data.pixData?.name || data.cardholderName || '',
        cpf: (data.cpf || data.pixData?.cpf || '').replace(/\D/g, ''),
        telefone: (data.telefone || data.pixData?.telefone || '').replace(/\D/g, ''),
      };

      if (method === 'pix') {
        // ===== FLUXO PIX (Mercado Pago) =====
        console.log('📱 Iniciando PIX (Mercado Pago)...');
        const result = await mercadoPagoService.payPix({ cartItems, payer });
        if (!result.success) {
          setStatusMessage({ type: 'error', message: result.error || 'Erro ao gerar QR Code PIX' });
          setIsProcessing(false);
          return;
        }
        setPixCopiaECola(result.qrCode);
        setQrCode(result.qrCode);
        setPaymentTxid(result.id);
        setStatusMessage({
          type: 'success',
          message: '✅ QR Code PIX gerado com sucesso! Escaneie para realizar o pagamento.',
        });

        // Libera a UI enquanto aguarda o pagamento (para o usuário poder cancelar
        // o PIX e escolher o cartão). O polling roda em segundo plano.
        setIsProcessing(false);
        pixAbortRef.current = false;

        console.log('⏳ Aguardando confirmação do PIX...');
        const aprovado = await waitMpApproval(result.id);
        if (pixAbortRef.current) {
          // PIX cancelado pelo usuário — não faz nada.
          return;
        }
        if (aprovado) {
          dispatch(grantEbookAccess({
            ebooks: ebookOnlyItems,
            paymentInfo: { method: 'pix', txid: result.id, email: payer.email, cpf: payer.cpf, name: payer.name },
          }));
          dispatch(authorizeMultipleDownloads({
            ebooks: ebookOnlyItems,
            paymentInfo: { method: 'pix', transactionId: result.id },
          }));
          await afterPaymentSuccess('pix', { mpPaymentId: String(result.id) }, data);
        } else {
          setStatusMessage({
            type: 'info',
            message: '⏱️ Aguardando o pagamento. Você pode consultar o status a qualquer momento.',
          });
        }
      } else if (method === 'credit_card') {
        // ===== FLUXO CARTÃO (Mercado Pago - Checkout Transparente) =====
        console.log('💳 Iniciando cartão (Mercado Pago)...');
        if (!data.cardholderName || !payer.email || !payer.cpf) {
          setStatusMessage({ type: 'error', message: '❌ Nome, e-mail e CPF são obrigatórios.' });
          setIsProcessing(false);
          return;
        }
        // O token já vem TOKENIZADO pelos Secure Fields (iframes do MP) no
        // PaymentOptions — o número do cartão NUNCA passou por aqui (PCI).
        const cardToken = data.cardToken;
        const bin = data.firstSixDigits;
        if (!cardToken) {
          setStatusMessage({ type: 'error', message: '❌ Confira os dados do cartão e tente novamente.' });
          setIsProcessing(false);
          return;
        }

        // Total do cartão (para consultar parcelas válidas) — catálogo do front.
        const { amount: cardAmount, maxInstallments } = priceCart(cartItems, 'card');

        let info;
        try {
          info = await getInstallmentsByBin({ bin, amount: cardAmount });
        } catch (e) {
          setStatusMessage({ type: 'error', message: e.message || 'Cartão inválido.' });
          setIsProcessing(false);
          return;
        }
        const desired = Number(data.installments) || 1;
        const permitidas = info.allowed.filter((n) => n <= maxInstallments);
        const finalInstallments = permitidas.includes(desired) ? desired : (permitidas[0] || 1);
        const paymentMethodId = info.paymentMethodId || (await getPaymentMethodByBin(bin));

        // Device ID (antifraude do MP) — melhora a aprovação do cartão.
        const deviceId = await getDeviceId();

        const result = await mercadoPagoService.payCard({
          cartItems,
          card: { token: cardToken, paymentMethodId, issuerId: info.issuerId, installments: finalInstallments },
          deviceId,
          payer,
        });

        const liberarCartaoAprovado = async () => {
          dispatch(grantEbookAccess({
            ebooks: ebookOnlyItems,
            paymentInfo: { method: 'credit_card', chargeId: result.id, email: payer.email, cpf: payer.cpf, name: payer.name },
          }));
          dispatch(authorizeMultipleDownloads({
            ebooks: ebookOnlyItems,
            paymentInfo: { method: 'credit_card', transactionId: result.id },
          }));
          await afterPaymentSuccess('credit_card', { mpPaymentId: String(result.id) }, data);
        };

        if (result?.success && result.status === 'approved') {
          await liberarCartaoAprovado();
        } else if (result?.success && (result.status === 'in_process' || result.status === 'pending')) {
          // Cartão em ANÁLISE do Mercado Pago — NÃO é recusa. Aguardamos aprovar.
          setStatusMessage({
            type: 'info',
            message: 'Seu pagamento está em análise pelo Mercado Pago. Assim que for aprovado (normalmente em alguns minutos), o acesso será liberado — você não precisa pagar de novo. Se preferir na hora, pague com PIX.',
          });
          // Polling em segundo plano (não trava a tela).
          (async () => {
            const aprovado = await waitMpApproval(result.id, { timeout: 10 * 60 * 1000 });
            if (aprovado) {
              await liberarCartaoAprovado();
            } else {
              setStatusMessage({
                type: 'error',
                message: 'O pagamento não foi aprovado. Tente outro cartão ou pague com PIX.',
              });
            }
          })();
        } else {
          setStatusMessage({
            type: 'error',
            message: friendlyMpStatus(result?.statusDetail) || result?.error || 'Pagamento não aprovado. Tente outro cartão ou pague com PIX.',
          });
        }
      }

    } catch (error) {
      console.error('❌ Erro ao processar pagamento:', error);
      setStatusMessage({
        type: 'error',
        message: error.message || 'Erro ao processar pagamento',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToProducts = () => {
    window.history.back();
  };

  // Preços dos produtos
  const collectionPrice = 7990; // R$ 79,90
  const tablaturasPrice = 3990; // R$ 39,90 (preço individual das tablaturas)

  // Verificar se há ebooks individuais no carrinho (1790 = R$ 17,90 no PIX)
  const hasIndividualEbooks = cartItems.some((item) => item.price === 1790);

  // Verificar se a coleção completa está no carrinho
  const hasCollection = cartItems.some((item) => item.id === 'colecao-completa-13-1');
  // Verificar se Tablaturas já está no carrinho
  const hasTablaturas = cartItems.some((item) => item.id === 'tablaturas');
  // Verificar se o Curso já está no carrinho
  const hasCourse = cartItems.some((item) => item.id === 'curso-fingerstyle-catolico');

  // Lógica de promoções:
  // 1. Promoção de coleção - mostrar quando há ebooks individuais (R$ 17,90) - SEM bundle
  const shouldShowCollectionPromo = hasIndividualEbooks && !hasCollection;

  // 2. Promoção de tablaturas - mostrar quando há coleção SEM tablaturas
  const showTablaturasPromo = hasCollection && !hasTablaturas;

  // 3. Promoção do curso para quem tem tablaturas - mostrar quando há tablaturas SEM curso
  const shouldShowCourseForTablaturasPromo = hasTablaturas && !hasCourse;

  // 4. Promoção de tablaturas para quem tem o curso - mostrar quando há curso SEM tablaturas
  const shouldShowTablaturasForCoursePromo = hasCourse && !hasTablaturas;

  // Bundle promocional foi removido para ebooks de R$ 17,90
  const shouldShowBundlePromo = false;

  const handleAddCollection = () => {
    // Limpa o carrinho e adiciona apenas a coleção completa
    dispatch(clearCart());
    dispatch(
      addToCart({
        id: 'colecao-completa-13-1',
        title: 'COLEÇÃO COMPLETA: 13 EM 1',
        price: collectionPrice,
        category: 'Coleções',
      })
    );
    showToast('Carrinho atualizado: apenas a Coleção Completa foi adicionada.');
  };

  // Nota: o comportamento de "trocar" foi removido da promoção.
  // A lógica de aplicar desconto nas Tablaturas permanece em `handleAddCollection`.

  const handleAddTablaturas = () => {
    // Se a coleção completa está no carrinho, aplicar desconto nas tablaturas
    const priceForTablaturas = hasCollection ? 2000 : tablaturasPrice; // R$ 20,00 se tem coleção (total 99,00), senão R$ 99,00
    
    dispatch(
      addToCart({
        id: 'tablaturas',
        title: 'Tablaturas católicas para Fingerstyle',
        price: priceForTablaturas,
        category: 'Técnica',
      })
    );
    
    const message = hasCollection
      ? 'Tablaturas adicionadas! Total do Pacote: R$ 99,00'
      : 'Tablaturas adicionadas ao carrinho!';

    showToast(message);
  };

  const handleAddBundlePromo = () => {
    // Limpar carrinho e adicionar coleção + tablaturas por R$ 99,00
    dispatch(clearCart());
    
    // Adicionar coleção completa
    dispatch(
      addToCart({
        id: 'colecao-completa-13-1',
        title: 'COLEÇÃO COMPLETA: 13 EM 1',
        price: collectionPrice, // R$ 79,90
        category: 'Coleções',
      })
    );
    
    // Adicionar tablaturas com desconto para completar o bundle de R$ 99,00
    dispatch(
      addToCart({
        id: 'tablaturas',
        title: 'Tablaturas católicas para Fingerstyle',
        price: 2000, // R$ 20,00 (desconto para completar bundle de R$ 99,00)
        category: 'Técnica',
      })
    );
    
    showToast('Pacote Super Especial adicionado! Coleção + Tablaturas por apenas R$ 99,00 (Economia de R$ 20,80)');
  };


  const handleAddCourseWithTablaturas = async () => {
    // O preço do curso é dinâmico (tier atual da loja-api). Usar um valor fixo
    // faz o loja-api rejeitar a compra (isCourseItemPriceValid), então garantimos
    // o preço real antes de adicionar o curso ao carrinho.
    let priceCents = coursePriceCents;
    if (!priceCents) {
      try {
        const c = await courseService.getCourse();
        priceCents = typeof c?.course?.priceCents === 'number' ? c.course.priceCents : null;
        if (priceCents) setCoursePriceCents(priceCents);
      } catch {
        priceCents = null;
      }
    }
    if (!priceCents) {
      setStatusMessage({
        type: 'error',
        message: 'Não foi possível carregar o preço do curso agora. Tente novamente em instantes.',
      });
      return;
    }
    // NÃO altera o preço das tablaturas: o desconto de R$ 20 é exclusivo do
    // combo com a Coleção Completa. Ao adicionar o curso, a tablatura mantém
    // o preço que já está no carrinho.
    dispatch(
      addToCart({
        id: 'curso-fingerstyle-catolico',
        type: 'course',
        title: 'Curso de Fingerstyle Católico',
        price: priceCents,
        category: 'Curso',
        image: '/capaEbooksLoja/capaCurso.png',
      })
    );
    const tablaturaItem = cartItems.find((i) => i.id === 'tablaturas');
    const tabPrice = tablaturaItem?.price ?? tablaturasPrice;
    const totalCombo = priceCents + tabPrice;
    // Se não estiver logado, o aviso (courseAuthNotice) explica o próximo passo —
    // não mostramos o toast de "adicionado" para não sobrepor o popup.
    if (isAuthenticated) {
      showToast(`Curso adicionado! Tablaturas + Curso por R$ ${(totalCombo / 100).toFixed(2).replace('.', ',')}.`);
    }
  };

  const handleAddTablaturasWithCourse = () => {
    dispatch(
      addToCart({
        id: 'tablaturas',
        title: 'Tablaturas católicas para Fingerstyle',
        price: tablaturasPrice,
        category: 'Técnica',
      })
    );
    const totalCombo = cartTotal + tablaturasPrice;
    showToast(`Tablaturas adicionadas! Curso + Tablaturas por R$ ${(totalCombo / 100).toFixed(2).replace('.', ',')}.`);
  };

  return (
    <CheckoutContainerStyle>
      <CheckoutContent>
        <CheckoutHeader>
          <h1>Checkout</h1>
          <p>Finalize sua compra</p>
        </CheckoutHeader>

        <PaymentResultModal
          open={!!resultModal}
          type={resultModal?.type}
          title={resultModal?.title}
          message={resultModal?.message}
          actionLabel={resultModal?.actionLabel}
          onAction={resultModal?.onAction}
          onClose={() => setResultModal(null)}
        />

        <Toast message={toast} onClose={() => setToast(null)} />

        {courseAuthNotice && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.72)',
              backdropFilter: 'blur(3px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: 16,
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 440,
                background: '#141414',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: 14,
                padding: '28px 24px',
                textAlign: 'center',
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.55)',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  margin: '0 auto 16px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 215, 0, 0.1)',
                  border: '2px solid #ffd700',
                  color: '#ffd700',
                  fontSize: 32,
                }}
              >
                <FiLock />
              </div>
              <h2 style={{ color: '#fff', fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>
                Crie sua conta para levar o curso
              </h2>
              <p style={{ color: '#cfcfcf', fontSize: 14, lineHeight: 1.5, margin: '0 0 22px' }}>
                O acesso ao curso fica liberado na sua conta, então você precisa entrar ou criar
                uma conta para continuar. Se preferir, remova o curso e compre só os eBooks (sem conta).
              </p>
              <button
                type="button"
                onClick={() => navigate('/login', { state: { from: '/checkout' } })}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#ffd700',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 800,
                  fontSize: 15,
                  cursor: 'pointer',
                  marginBottom: 10,
                }}
              >
                Entrar / Criar conta
              </button>
              <button
                type="button"
                onClick={() => {
                  const courseItem = cartItems.find((i) => i.type === 'course');
                  if (courseItem) handleRemoveFromCart(courseItem.id);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  color: '#cfcfcf',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Remover curso e continuar
              </button>
            </div>
          </div>
        )}

        {/* Erros aparecem no modal centralizado (PaymentResultModal); aqui só
            status de sucesso/info, como um toast fixo animado. */}
        {statusMessage && statusMessage.type !== 'error' && (
          <StatusMessage type={statusMessage.type}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              {statusMessage.type === 'success' ? (
                <FiCheckCircle style={{ flexShrink: 0 }} />
              ) : statusMessage.type === 'error' ? (
                <FiAlertCircle style={{ flexShrink: 0 }} />
              ) : (
                <FiInfo style={{ flexShrink: 0 }} />
              )}
              <span>{statusMessage.message.replace(/^[^\p{L}\p{N}]+/u, '')}</span>
            </span>
          </StatusMessage>
        )}

        {pendingAccount && (
          <AccountPromptBox>
            <h3><FiLock style={{ verticalAlign: '-2px', marginRight: 6 }} />Crie sua conta para acessar {purchasedItemsNoun}</h3>
            <p>
              Para garantir acesso a {purchasedItemsNoun} em qualquer dispositivo, crie uma conta agora.
              Você já forneceu o e-mail abaixo — só precisa definir uma senha.
            </p>
            <div className="email-row">
              E-mail: <strong>{pendingAccount.email}</strong>
            </div>
            {accountError === '409' ? (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <p style={{ color: '#f59e0b', marginBottom: 12 }}>
                  Esse e-mail já tem uma conta cadastrada.
                </p>
                <Link
                  to="/login"
                  state={{ from: successDestination }}
                  style={{
                    display: 'inline-block',
                    padding: '12px 24px',
                    backgroundColor: '#ffd700',
                    color: '#1a1a1a',
                    borderRadius: 6,
                    fontWeight: 700,
                    textDecoration: 'none',
                  }}
                >
                  Fazer login para acessar {purchasedItemsNoun} <FiArrowRight style={{ verticalAlign: '-2px' }} />
                </Link>
              </div>
            ) : (
              <form onSubmit={handleCreateAccount} noValidate>
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="WhatsApp com DDD — (11) 91234-5678"
                  value={formatWhatsapp(accountWhatsapp)}
                  onChange={(e) => setAccountWhatsapp(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  autoFocus
                  disabled={accountSubmitting}
                />
                <input
                  type="password"
                  placeholder="Defina uma senha (mín. 8 caracteres)"
                  value={accountPassword}
                  onChange={(e) => setAccountPassword(e.target.value)}
                  minLength={8}
                  disabled={accountSubmitting}
                />
                {accountError && <div className="error">{accountError}</div>}
                <div className="actions">
                  <button type="submit" className="primary" disabled={accountSubmitting}>
                    {accountSubmitting ? 'Criando…' : `Criar conta e acessar ${purchasedItemsNoun}`}
                  </button>
                </div>
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <Link
                    to="/login"
                    state={{ from: successDestination }}
                    style={{ color: '#aaa', fontSize: 13 }}
                  >
                    Já tenho conta — fazer login
                  </Link>
                </div>
              </form>
            )}
          </AccountPromptBox>
        )}

        <CheckoutGrid>
          {/* Coluna esquerda: CARRINHO + ITENS DO CARRINHO */}
          <CheckoutSection>
            <Cart
              items={cartItems}
              onRemove={handleRemoveFromCart}
              onUpdateQuantity={handleUpdateQuantity}
              totalPrice={displayTotalCents}
            />

            {cartItems.length > 0 && (
              <CartItemsGrid>
                <div className='grid-title'>
                  <FiShoppingBag style={{ verticalAlign: '-2px', marginRight: 6 }} />
                  Itens do Carrinho
                </div>
                <div className='items-grid'>
                  {cartItems.map((item) => (
                    <div key={item.id} className='item-card'>
                      <div className='item-image'>
                        <img
                          src={PRODUCTS_IMAGES[item.id] || '/capaEbooksLoja/placeholder.png'}
                          alt={item.title}
                          onError={(e) => {
                            e.target.src = '/capaEbooksLoja/placeholder.png';
                          }}
                        />
                      </div>
                      <div className='item-info'>
                        <div className='item-name'>{item.title}</div>
                        <div className='item-price'>{brl(unitPriceCents(item.id, displayMethod))}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CartItemsGrid>
            )}

            {/* Só no celular: pula para a área de pagamento (que fica embaixo). */}
            {cartItems.length > 0 && (
              <GoToPaymentButton
                onClick={() =>
                  paymentOptionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              >
                <FiArrowDown style={{ verticalAlign: '-2px', marginRight: 6 }} />
                IR PARA PAGAMENTO
              </GoToPaymentButton>
            )}
          </CheckoutSection>

          {/* Coluna direita: OFERTAS + MÉTODOS DE PAGAMENTO (form em modal) */}
          <CheckoutSection>
            {shouldShowBundlePromo && (
              <PromotionalBox>
                <div className='promo-label'><FiTarget style={{ verticalAlign: '-2px', marginRight: 5 }} />SUPER OFERTA</div>
                <div className='promo-swap-title'>Coleção Completa + Tablaturas</div>
                <div className='promo-swap-subtitle'>
                  Você escolheu {cartItems.length} {cartItems.length === 1 ? 'ebook' : 'ebooks'}. Leve a Coleção Completa (13 ebooks) + 20 Tablaturas por apenas R$ 99,00!
                </div>

                <div className='swap-row'>
                  <div className='choice'>
                    <div>
                      <div className='choice-title'>APENAS EBOOKS</div>
                      <div className='choice-price'>{brl(displayTotalCents)}</div>
                      <div style={{fontSize:12, color:Colors.textSecondary, marginTop:6}}>{cartItems.length} {cartItems.length === 1 ? 'ebook' : 'ebooks'}</div>
                    </div>
                  </div>

                  <div className='arrow-button' title='Upgrade para bundle' onClick={handleAddBundlePromo}><FiArrowRight /></div>

                  <div className='choice'>
                    <div>
                      <div className='choice-title'>BUNDLE ESPECIAL</div>
                      <div className='choice-price'>R$ 99,00</div>
                      <div style={{fontSize:12, color:Colors.textSecondary, marginTop:6}}>13 ebooks + 20 tablaturas</div>
                    </div>
                  </div>
                </div>

                <div className='promo-summary'>
                  <div className='summary-badge'>De R$ 119,80 por R$ 99,00</div>
                  <div style={{color:Colors.textSecondary}}>Economia de R$ 20,80 (44%)</div>
                </div>

                <button className='promo-cta' onClick={handleAddBundlePromo}>GARANTIR PACOTE POR R$ 99,00</button>

                <div className='promo-footer-note'><FiCheck style={{ verticalAlign: '-2px', marginRight: 4 }} />Vamos substituir o conteúdo do seu carrinho pelo pacote completo</div>
              </PromotionalBox>
            )}

            {shouldShowCollectionPromo && !showTablaturasPromo && (
              <PromotionalBox>
                <div className='promo-label'><FiTag style={{ verticalAlign: '-2px', marginRight: 5 }} />MELHOR ESCOLHA</div>
                <div className='bump-header'>
                  <div className='bump-icon'><FiBookOpen /></div>
                  <div className='bump-info'>
                    <div className='bump-title'>Troque pelo pacote completo</div>
                    <div className='bump-price'>R$ 79,90</div>
                    <div className='bump-desc'>13 ebooks • Apenas R$ 6,15 por unidade.</div>
                  </div>
                </div>
                <div className='bump-features'>
                  <div className='bump-feature'><FiCheck style={{ verticalAlign: '-2px', marginRight: 4, color: '#22c55e' }} />13 ebooks pelo preço de poucos</div>
                  <div className='bump-feature'><FiCheck style={{ verticalAlign: '-2px', marginRight: 4, color: '#22c55e' }} />Economia de até R$ 75,97 (49%)</div>
                </div>
                <button className='promo-cta' onClick={handleAddCollection}>TROCAR PELO PACOTE COMPLETO</button>
              </PromotionalBox>
            )}
            
            {showTablaturasPromo && (
              <PromotionalBox>
                <div className='promo-label'><FiMusic style={{ verticalAlign: '-2px', marginRight: 5 }} />COMPLEMENTO ESPECIAL</div>
                <div className='bump-header'>
                  <div className='bump-icon'><FiMusic /></div>
                  <div className='bump-info'>
                    <div className='bump-title'>Adicione as tablaturas</div>
                    <div className='bump-price'>+R$ 20,00</div>
                    <div className='bump-desc'>20 músicas fingerstyle para praticar com a coleção.</div>
                  </div>
                </div>
                <div className='bump-features'>
                  <div className='bump-feature'><FiCheck style={{ verticalAlign: '-2px', marginRight: 4, color: '#22c55e' }} />Material complementar à coleção</div>
                  <div className='bump-feature'><FiCheck style={{ verticalAlign: '-2px', marginRight: 4, color: '#22c55e' }} />Oferta exclusiva do pacote</div>
                </div>
                <button className='promo-cta' onClick={handleAddTablaturas}>ADICIONAR AO PEDIDO</button>
              </PromotionalBox>
            )}

            {shouldShowCourseForTablaturasPromo && (
              <PromotionalBox>
                <div className='promo-label'><FiAward style={{ verticalAlign: '-2px', marginRight: 5 }} />SUPER COMPLEMENTO</div>
                <div className='bump-header'>
                  <div className='bump-icon'><FiAward /></div>
                  <div className='bump-info'>
                    <div className='bump-title'>Adicione o Curso de Fingerstyle</div>
                    <div className='bump-price'>+{brl(unitPriceCents('curso-fingerstyle-catolico', 'pix'))}</div>
                    <div className='bump-desc'>Do básico ao avançado, com vídeo aulas completas.</div>
                  </div>
                </div>
                <div className='bump-features'>
                  <div className='bump-feature'><FiCheck style={{ verticalAlign: '-2px', marginRight: 4, color: '#22c55e' }} />Acesso vitalício ao curso</div>
                </div>
                <button className='promo-cta' onClick={handleAddCourseWithTablaturas}>ADICIONAR CURSO AO PACOTE</button>
              </PromotionalBox>
            )}

            {shouldShowTablaturasForCoursePromo && (
              <PromotionalBox>
                <div className='promo-label'><FiMusic style={{ verticalAlign: '-2px', marginRight: 5 }} />COMPLEMENTO ESPECIAL</div>
                <div className='bump-header'>
                  <div className='bump-icon'><FiMusic /></div>
                  <div className='bump-info'>
                    <div className='bump-title'>Adicione as tablaturas</div>
                    <div className='bump-price'>R$ 39,90</div>
                    <div className='bump-desc'>20 músicas fingerstyle para praticar o conteúdo do curso.</div>
                  </div>
                </div>
                <div className='bump-features'>
                  <div className='bump-feature'><FiCheck style={{ verticalAlign: '-2px', marginRight: 4, color: '#22c55e' }} />Material complementar do curso</div>
                  <div className='bump-feature'><FiCheck style={{ verticalAlign: '-2px', marginRight: 4, color: '#22c55e' }} />Oferta exclusiva do pacote</div>
                </div>
                <button className='promo-cta' onClick={handleAddTablaturasWithCourse}>ADICIONAR AO PEDIDO</button>
              </PromotionalBox>
            )}

            {cartItems.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  background: 'rgba(255,215,0,0.08)',
                  border: '1px solid rgba(255,215,0,0.35)',
                  borderRadius: 10,
                  padding: '12px 14px',
                  margin: '0 0 16px',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: Colors.textPrimary,
                }}
              >
                <FiInfo style={{ flexShrink: 0, marginTop: 2, color: Colors.gold }} />
                <div>
                  <strong>Sobre os preços:</strong> no <strong>PIX</strong> você paga{' '}
                  <strong>{brl(priceCart(cartItems, 'pix').totalCents)}</strong>. No{' '}
                  <strong>cartão</strong> o valor é <strong>{brl(cardPricing.totalCents)}</strong>
                  {cardPricing.maxInstallments > 1
                    ? `, podendo parcelar em até ${cardPricing.maxInstallments}x`
                    : ' (somente à vista)'}
                  . O total no resumo muda conforme o método que você escolher.
                </div>
              </div>
            )}

            <PaymentOptions
              ref={paymentOptionsRef}
              onPixSelect={handlePixSelect}
              onCardSelect={handleCardSelect}
              onPaymentConfirm={handlePaymentConfirm}
              isProcessing={isProcessing}
              qrCode={qrCode}
              pixCopiaECola={pixCopiaECola}
              paymentTxid={paymentTxid}
              onCancelPayment={handleCancelPayment}
              cartTotal={displayTotalCents}
              pixTotal={displayPricing.totalCents}
              cardTotal={cardPricing.totalCents}
              maxInstallments={cardPricing.maxInstallments}
            />
          </CheckoutSection>
        </CheckoutGrid>
  {/* 
          <p style={{
            textAlign: 'center',
            fontSize: '11px',
            color: 'rgba(255,255,255,0.3)',
            margin: '8px 0 0',
            lineHeight: '1.5',
          }}>
            Ao concluir sua compra, você declara que leu e está de acordo com os{' '}
            <a
              href="/termo-de-uso-e-politica-de-privacidade"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgba(255,215,0,0.6)', textDecoration: 'underline' }}
            >
              termos de uso e política de privacidade
            </a>
            .
          </p> */}

        <BackButton onClick={handleBackToProducts}><FiArrowLeft style={{ verticalAlign: '-2px', marginRight: 4 }} />Voltar</BackButton>
      </CheckoutContent>
    </CheckoutContainerStyle>
  );
};

export default Checkout;
